// src/components/OS/OSDetalheModal.jsx
import React, { useMemo, useState } from "react";
import {
  tempoDesde as utilsTempoDesde,
  formatarData as utilsFormatarData,
} from "../../utils/format";
import { Clipboard, Check, AlertTriangle } from "lucide-react";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

// Normaliza URL quando a Focus devolve apenas o caminho
const ensureFocusAbsolute = (maybePath) =>
  maybePath && !/^https?:\/\//i.test(maybePath)
    ? `https://api.focusnfe.com.br${maybePath}`
    : maybePath || "";

// Detecta URL/caminho da Focus
const looksLikeFocus = (u = "") =>
  /focusnfe\.com\.br/i.test(u) || /^\//.test(u);

/* ---------- tradutores simples (compatíveis com a listagem) ---------- */
const traduzirStatusNfse = (status) => {
  switch (status) {
    case "autorizada":
    case "aprovada":
    case "emitida":
      return "Emitida";
    case "processando_autorizacao":
      return "Aguardando autorização";
    case "rejeitada":
    case "erro":
      return "Erro na emissão";
    case "cancelada":
      return "Cancelada";
    case "nao_emitida":
    case undefined:
    default:
      return "Não emitida";
  }
};

const traduzirStatusNfe = (status) => {
  switch ((status || "").toLowerCase()) {
    case "autorizado":
    case "autorizada":
    case "emitida":
    case "aprovada":
      return "Emitida";
    case "processando_autorizacao":
      return "Aguardando autorização";
    case "rejeitada":
    case "erro":
      return "Erro na emissão";
    case "cancelada":
      return "Cancelada";
    case "":
    case undefined:
    default:
      return "Não emitida";
  }
};

/* ---------- view helpers para chips ---------- */
const nfStatusView = (status = "") => {
  const s = String(status || "").toLowerCase();
  if (!s)
    return {
      text: "Não emitida",
      badge: "bg-gray-100 text-gray-700 border-gray-200",
      dot: "bg-gray-400",
    };
  if (s.includes("autoriz"))
    return {
      text: "Autorizada",
      badge: "bg-green-100 text-green-700 border-green-200",
      dot: "bg-green-500",
    };
  if (s.includes("cancelad"))
    return {
      text: "Cancelada",
      badge: "bg-gray-100 text-gray-700 border-gray-200",
      dot: "bg-gray-500",
    };
  if (s.includes("deneg"))
    return {
      text: "Denegada",
      badge: "bg-red-100 text-red-700 border-red-200",
      dot: "bg-red-500",
    };
  if (s.includes("erro") || s.includes("rejei") || s.includes("falha"))
    return {
      text: "Rejeitada",
      badge: "bg-red-100 text-red-700 border-red-200",
      dot: "bg-red-500",
    };
  if (s.includes("process") || s.includes("aguard") || s.includes("pend"))
    return {
      text: "Aguardando autorização",
      badge: "bg-yellow-100 text-yellow-800 border-yellow-200",
      dot: "bg-yellow-500",
    };
  return {
    text: status,
    badge: "bg-yellow-100 text-yellow-800 border-yellow-200",
    dot: "bg-yellow-500",
  };
};

const nfseStatusView = (status = "") => {
  const s = String(status || "").toLowerCase();
  if (!s)
    return {
      text: "Não emitida",
      badge: "bg-gray-100 text-gray-700 border-gray-200",
      dot: "bg-gray-400",
    };
  if (s.includes("cancelad"))
    return {
      text: "Cancelada",
      badge: "bg-gray-100 text-gray-700 border-gray-200",
      dot: "bg-gray-500",
    };
  if (s.includes("autoriz") || s.includes("emitida") || s.includes("aprov"))
    return {
      text: "Emitida",
      badge: "bg-green-100 text-green-700 border-green-200",
      dot: "bg-green-500",
    };
  if (s.includes("erro") || s.includes("rejei") || s.includes("falha"))
    return {
      text: "Rejeitada",
      badge: "bg-red-100 text-red-700 border-red-200",
      dot: "bg-red-500",
    };
  if (s.includes("process") || s.includes("aguard") || s.includes("pend"))
    return {
      text: "Aguardando autorização",
      badge: "bg-yellow-100 text-yellow-800 border-yellow-200",
      dot: "bg-yellow-500",
    };
  return {
    text: status,
    badge: "bg-yellow-100 text-yellow-800 border-yellow-200",
    dot: "bg-yellow-500",
  };
};

const Chip = ({ text, className = "", dotClass = "" }) => (
  <span
    className={`inline-flex items-center gap-2 text-xs font-semibold px-2 py-1 rounded border ${className}`}
  >
    <i className={`w-2 h-2 rounded-full ${dotClass}`} />
    {text}
  </span>
);

const truncate = (str, n = 140) =>
  !str ? "" : str.length > n ? `${str.slice(0, n)}…` : str;

// se houver erro, o chip vira "Rejeitada"
const asErrorView = (view, hasError) =>
  hasError
    ? {
        text: "Rejeitada",
        badge: "bg-red-100 text-red-700 border-red-200",
        dot: "bg-red-500",
      }
    : view;

/* ----------------------------------------------------------------------------- */

const OSDetalheModal = ({
  open,
  os,
  onClose,
  statusColors = {},
  tempoDesde = utilsTempoDesde,
  formatarData = utilsFormatarData,
}) => {
  const [copiadoResumo, setCopiadoResumo] = useState(false);
  const [copiadoChave, setCopiadoChave] = useState(false);
  const [copiadoLinkNfse, setCopiadoLinkNfse] = useState(false);
  const [copiadoDanfe, setCopiadoDanfe] = useState(false);
  const [copiadoXml, setCopiadoXml] = useState(false);
  const [copiadoPdf, setCopiadoPdf] = useState(false);
  const [copiadoHtml, setCopiadoHtml] = useState(false);

  if (!open || !os) return null;

  const produtos = Array.isArray(os.produtos) ? os.produtos : [];
  const servicos = Array.isArray(os.servicos) ? os.servicos : [];

  const totalProdutos = Number(os.valorTotalProdutos || 0);
  const totalServicos = Number(os.valorTotalServicos || 0);
  const totalGeral = Number(os.valorTotalGeral || 0);

  const temData = !!os.dataAbertura;
  const statusCls = statusColors?.[os.status] || "";

  // ---------- NF-e: prioriza rotas do backend (aceita CHAVE ou REF) ----------
  const nfeChaveClean = (os.nfeChave || "").replace(/^NFe/, "");
  const nfeRef = os.nfeRef || "";
  const nfeId = encodeURIComponent(nfeChaveClean || nfeRef || "");

  const savedDanfe = ensureFocusAbsolute(os.nfeDanfeUrl || "");
  const savedXml = ensureFocusAbsolute(os.nfeXmlUrl || "");

  const danfeUrl =
    (nfeId ? `${BACKEND_URL}/api/focus/baixar-danfe/${nfeId}` : "") ||
    (!looksLikeFocus(savedDanfe) ? savedDanfe : "");

  const xmlUrl =
    (nfeId ? `${BACKEND_URL}/api/focus/baixar-xml/${nfeId}` : "") ||
    (!looksLikeFocus(savedXml) ? savedXml : "");

  // ---------- NFS-e: viewer do backend quando link é da Focus (ou quando só temos ref) ----------
  const nfseRef = os.nfseRef || "";
  const nfseViewer = nfseRef
    ? `${BACKEND_URL}/api/focus/visualizar-nfse/${encodeURIComponent(nfseRef)}`
    : "";
  const savedNfseLink = ensureFocusAbsolute(os.nfseUrl || "");
  const nfseUrl =
    (looksLikeFocus(savedNfseLink) ? nfseViewer : savedNfseLink) ||
    nfseViewer ||
    "";

  // PDF/HTML (se persistidos no OS.jsx)
  const nfsePdfUrl = ensureFocusAbsolute(os.nfsePdfUrl || "");
  const nfseHtmlUrl = ensureFocusAbsolute(os.nfseHtmlUrl || "");

  // Status amigável + views p/ chips (erro tem precedência)
  const nfseStatusFriendly = os.nfseUltimoErro
    ? "Erro na emissão"
    : traduzirStatusNfse(os.nfseStatus);
  const nfeStatusFriendly = os.nfeUltimoErro
    ? "Erro na emissão"
    : traduzirStatusNfe(os.nfeStatus);

  const nfseV = asErrorView(nfseStatusView(os.nfseStatus), !!os.nfseUltimoErro);
  const nfeV = asErrorView(nfStatusView(os.nfeStatus), !!os.nfeUltimoErro);

  // Texto do resumo p/ copiar
  const resumoTexto = useMemo(() => {
    const linhas = [];

    linhas.push(`OS #${os.numeroOS ?? "..."}`);
    linhas.push(`Status: ${os.status || "-"}`);
    if (temData) {
      linhas.push(
        `Abertura: ${formatarData(os.dataAbertura)} (há ${tempoDesde(
          os.dataAbertura
        )})`
      );
    }

    linhas.push("");
    linhas.push("== Dados do Cliente ==");
    linhas.push(`Cliente: ${os.clienteNome || "-"}`);

    linhas.push("");
    linhas.push("== Veículo ==");
    linhas.push(`${os.veiculoDescricao || "-"}`);

    linhas.push("");
    linhas.push("== Produtos ==");
    if (produtos.length === 0) {
      linhas.push("Nenhum produto utilizado.");
    } else {
      produtos.forEach((p) => {
        const qtd = Number(p.quantidade || 0);
        const unit = Number(p.precoVenda || 0);
        const tot = (qtd * unit).toFixed(2);
        linhas.push(
          `- ${p.nome || "-"} | Qtd: ${qtd} | Unit: R$ ${unit.toFixed(
            2
          )} | Total: R$ ${tot}`
        );
      });
    }

    linhas.push("");
    linhas.push("== Serviços ==");
    if (servicos.length === 0) {
      linhas.push("Nenhum serviço adicionado.");
    } else {
      servicos.forEach((s) => {
        const v = Number(s.valor || 0);
        linhas.push(`- ${s.descricao || "-"} | Valor: R$ ${v.toFixed(2)}`);
      });
    }

    linhas.push("");
    linhas.push("== Totais ==");
    linhas.push(`Produtos: R$ ${totalProdutos.toFixed(2)}`);
    linhas.push(`Serviços: R$ ${totalServicos.toFixed(2)}`);
    linhas.push(`TOTAL: R$ ${totalGeral.toFixed(2)}`);

    linhas.push("");
    linhas.push("== Notas Fiscais ==");
    // NFS-e
    linhas.push(`NFS-e: ${nfseStatusFriendly}`);
    linhas.push(`  Ref: ${os.nfseRef || "-"}`);
    linhas.push(`  Nº/Série: ${os.nfseNumero || "-"} / ${os.nfseSerie || "-"}`);
    linhas.push(`  Link: ${nfseUrl || "-"}`);
    if (nfsePdfUrl) linhas.push(`  PDF: ${nfsePdfUrl}`);
    if (nfseHtmlUrl) linhas.push(`  HTML: ${nfseHtmlUrl}`);
    if (os.nfseUltimoErro) {
      linhas.push(`  Erro NFS-e: ${os.nfseUltimoErro}`);
    }
    // NF-e
    linhas.push(`NF-e: ${nfeStatusFriendly}`);
    linhas.push(`  Chave/Ref: ${nfeChaveClean || nfeRef || "-"}`);
    linhas.push(`  Nº/Série: ${os.nfeNumero || "-"} / ${os.nfeSerie || "-"}`);
    linhas.push(`  DANFe: ${danfeUrl || "-"}`);
    linhas.push(`  XML: ${xmlUrl || "-"}`);
    if (os.nfeUltimoErro) {
      linhas.push(`  Erro NF-e: ${os.nfeUltimoErro}`);
    }

    linhas.push("");
    linhas.push("== Observações ==");
    linhas.push(os.observacoes ? String(os.observacoes) : "Nenhuma observação.");

    return linhas.join("\n");
  }, [
    os,
    produtos,
    servicos,
    temData,
    totalProdutos,
    totalServicos,
    totalGeral,
    formatarData,
    tempoDesde,
    nfseStatusFriendly,
    nfeStatusFriendly,
    nfeChaveClean,
    nfeRef,
    danfeUrl,
    xmlUrl,
    nfseUrl,
    nfsePdfUrl,
    nfseHtmlUrl,
  ]);

  const copiar = async (texto, setFlag) => {
    try {
      await navigator.clipboard.writeText(texto);
      setFlag(true);
      setTimeout(() => setFlag(false), 1600);
    } catch {
      const area = document.createElement("textarea");
      area.value = texto;
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
        setFlag(true);
        setTimeout(() => setFlag(false), 1600);
      } finally {
        document.body.removeChild(area);
      }
    }
  };

  const copiarResumo = () => copiar(resumoTexto, setCopiadoResumo);
  const copiarChave = () =>
    (nfeChaveClean || nfeRef) && copiar(nfeChaveClean || nfeRef, setCopiadoChave);
  const copiarLinkNfse = () => nfseUrl && copiar(nfseUrl, setCopiadoLinkNfse);
  const copiarDanfe = () => danfeUrl && copiar(danfeUrl, setCopiadoDanfe);
  const copiarXml = () => xmlUrl && copiar(xmlUrl, setCopiadoXml);
  const copiarPdf = () => nfsePdfUrl && copiar(nfsePdfUrl, setCopiadoPdf);
  const copiarHtml = () => nfseHtmlUrl && copiar(nfseHtmlUrl, setCopiadoHtml);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-3xl relative animate-modalSlideIn border border-gray-100 dark:border-gray-800">
        {/* Header */}
        <div className="p-6 pb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-[#004072] dark:text-white">
              Detalhes da OS{" "}
              <span className="text-base font-semibold ml-3 bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200 rounded-full px-3 py-1">
                #{os.numeroOS ?? "..."}
              </span>
            </h2>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span
                className={`inline-block text-xs font-semibold px-2 py-1 rounded border ${statusCls}`}
              >
                {os.status || "-"}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                Aberta em: {temData ? formatarData(os.dataAbertura) : "-"}
                {temData && <> (há {tempoDesde(os.dataAbertura)})</>}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={copiarResumo}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              title="Copiar resumo"
            >
              {copiadoResumo ? <Check size={18} /> : <Clipboard size={18} />}
              {copiadoResumo ? "Copiado!" : "Copiar resumo"}
            </button>

            <button
              className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
              onClick={onClose}
            >
              Fechar
            </button>
          </div>
        </div>

        {/* Conteúdo com scroll */}
        <div className="p-6 pt-2 space-y-6 dark:text-gray-100 max-h-[75vh] overflow-y-auto">
          {/* Duas colunas: Cliente / Veículo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-xl p-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <div className="font-semibold mb-2">Dados do Cliente</div>
              <div className="text-sm">
                <div>
                  <span className="font-medium">Nome: </span>
                  {os.clienteNome || "-"}
                </div>
              </div>
            </div>
            <div className="border rounded-xl p-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <div className="font-semibold mb-2">Dados do Veículo</div>
              <div className="text-sm">{os.veiculoDescricao || "-"}</div>
            </div>
          </div>

          {/* Produtos usados */}
          <div>
            <div className="font-semibold mb-1">Produtos usados</div>
            {produtos.length === 0 ? (
              <span className="text-sm text-gray-400">Nenhum produto utilizado.</span>
            ) : (
              <div className="border rounded-xl p-2 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                {produtos.map((p, idx) => {
                  const qtd = Number(p.quantidade || 0);
                  const unit = Number(p.precoVenda || 0);
                  const tot = unit * qtd;
                  return (
                    <div
                      key={idx}
                      className="flex gap-2 text-sm border-b last:border-b-0 border-gray-200 dark:border-gray-700 py-1 flex-wrap"
                    >
                      <span className="font-semibold">{p.nome || "-"}</span>
                      <span>Qtd: {qtd}</span>
                      <span>Unit: R$ {unit.toFixed(2)}</span>
                      <span>
                        Total: <b>R$ {tot.toFixed(2)}</b>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Serviços prestados */}
          <div>
            <div className="font-semibold mb-1">Serviços prestados</div>
            {servicos.length === 0 ? (
              <span className="text-sm text-gray-400">Nenhum serviço adicionado.</span>
            ) : (
              <div className="border rounded-xl p-2 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                {servicos.map((s, idx) => {
                  const v = Number(s.valor || 0);
                  return (
                    <div
                      key={idx}
                      className="flex gap-2 text-sm border-b last:border-b-0 border-gray-200 dark:border-gray-700 py-1 flex-wrap"
                    >
                      <span className="font-semibold">{s.descricao || "-"}</span>
                      <span>
                        Valor: <b>R$ {v.toFixed(2)}</b>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notas Fiscais */}
          <div>
            <div className="font-semibold mb-1">Notas Fiscais</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {/* NFS-e */}
              <div className="border rounded-xl p-3 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                <div className="font-medium mb-2 flex items-center gap-2">
                  <Chip text={nfseV.text} className={nfseV.badge} dotClass={nfseV.dot} />
                  {nfseV.text === "Aguardando autorização" && !os.nfseUltimoErro && (
                    <span className="text-blue-700 dark:text-blue-300 animate-pulse">
                      Aguardando…
                    </span>
                  )}
                </div>
                <div>Ref: {os.nfseRef || "-"}</div>
                <div>
                  Nº/Série: {os.nfseNumero || "-"} / {os.nfseSerie || "-"}
                </div>

                {/* Links NFS-e */}
                <div className="mt-2 space-y-1">
                  <div className="truncate flex items-center gap-2">
                    <span className="shrink-0">Viewer:</span>
                    {nfseUrl ? (
                      <>
                        <a
                          className="text-blue-700 dark:text-blue-300 underline truncate"
                          href={nfseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={nfseUrl}
                        >
                          {nfseUrl}
                        </a>
                        <button
                          className="px-2 py-1 text-xs rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={copiarLinkNfse}
                          title="Copiar link da NFS-e"
                        >
                          {copiadoLinkNfse ? <Check size={14} /> : <Clipboard size={14} />}
                        </button>
                      </>
                    ) : (
                      "-"
                    )}
                  </div>

                  {nfsePdfUrl && (
                    <div className="truncate flex items-center gap-2">
                      <span className="shrink-0">PDF:</span>
                      <a
                        className="text-blue-700 dark:text-blue-300 underline truncate"
                        href={nfsePdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={nfsePdfUrl}
                      >
                        {nfsePdfUrl}
                      </a>
                      <button
                        className="px-2 py-1 text-xs rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={copiarPdf}
                        title="Copiar link do PDF"
                      >
                        {copiadoPdf ? <Check size={14} /> : <Clipboard size={14} />}
                      </button>
                    </div>
                  )}

                  {nfseHtmlUrl && (
                    <div className="truncate flex items-center gap-2">
                      <span className="shrink-0">HTML:</span>
                      <a
                        className="text-blue-700 dark:text-blue-300 underline truncate"
                        href={nfseHtmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={nfseHtmlUrl}
                      >
                        {nfseHtmlUrl}
                      </a>
                      <button
                        className="px-2 py-1 text-xs rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={copiarHtml}
                        title="Copiar link do HTML"
                      >
                        {copiadoHtml ? <Check size={14} /> : <Clipboard size={14} />}
                      </button>
                    </div>
                  )}
                </div>

                {!!os.nfseUltimoErro && (
                  <div className="mt-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-2 flex items-start gap-2">
                    <AlertTriangle size={16} className="mt-[2px]" />
                    <div>
                      <div className="font-semibold">Erro na NFS-e</div>
                      <div title={os.nfseUltimoErro}>{truncate(os.nfseUltimoErro)}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* NF-e */}
              <div className="border rounded-xl p-3 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                <div className="font-medium mb-2 flex items-center gap-2">
                  <Chip text={nfeV.text} className={nfeV.badge} dotClass={nfeV.dot} />
                  {nfeV.text === "Aguardando autorização" && !os.nfeUltimoErro && (
                    <span className="text-green-700 dark:text-green-300 animate-pulse">
                      Aguardando…
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="shrink-0">Chave/Ref:</span>
                  <span className="truncate" title={nfeChaveClean || nfeRef || "-"}>
                    {nfeChaveClean || nfeRef || "-"}
                  </span>
                  {(nfeChaveClean || nfeRef) && (
                    <button
                      className="px-2 py-1 text-xs rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={copiarChave}
                      title="Copiar chave/ref da NF-e"
                    >
                      {copiadoChave ? <Check size={14} /> : <Clipboard size={14} />}
                    </button>
                  )}
                </div>
                <div>
                  Nº/Série: {os.nfeNumero || "-"} / {os.nfeSerie || "-"}
                </div>

                <div className="truncate flex items-center gap-2">
                  <span className="shrink-0">DANFe:</span>
                  {danfeUrl ? (
                    <>
                      <a
                        className="text-blue-700 dark:text-blue-300 underline"
                        href={danfeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={danfeUrl}
                      >
                        {danfeUrl}
                      </a>
                      <button
                        className="px-2 py-1 text-xs rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={copiarDanfe}
                        title="Copiar link do DANFe"
                      >
                        {copiadoDanfe ? <Check size={14} /> : <Clipboard size={14} />}
                      </button>
                    </>
                  ) : (
                    "-"
                  )}
                </div>

                <div className="truncate flex items-center gap-2">
                  <span className="shrink-0">XML:</span>
                  {xmlUrl ? (
                    <>
                      <a
                        className="text-blue-700 dark:text-blue-300 underline"
                        href={xmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={xmlUrl}
                      >
                        {xmlUrl}
                      </a>
                      <button
                        className="px-2 py-1 text-xs rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={copiarXml}
                        title="Copiar link do XML"
                      >
                        {copiadoXml ? <Check size={14} /> : <Clipboard size={14} />}
                      </button>
                    </>
                  ) : (
                    "-"
                  )}
                </div>

                {!!os.nfeUltimoErro && (
                  <div className="mt-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-2 flex items-start gap-2">
                    <AlertTriangle size={16} className="mt-[2px]" />
                    <div>
                      <div className="font-semibold">Erro na NF-e</div>
                      <div title={os.nfeUltimoErro}>{truncate(os.nfeUltimoErro)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Observações */}
          <div>
            <span className="font-semibold">Observações</span>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-3 mt-1 text-sm">
              {os.observacoes ? (
                os.observacoes
              ) : (
                <span className="text-gray-400">Nenhuma observação</span>
              )}
            </div>
          </div>

          {/* Totais */}
          <div className="flex flex-wrap gap-4 mt-2">
            <span>
              Produtos: <b>R$ {totalProdutos.toFixed(2)}</b>
            </span>
            <span>
              Serviços: <b>R$ {totalServicos.toFixed(2)}</b>
            </span>
            <span>
              Total:{" "}
              <b className="text-green-700 dark:text-green-400">
                R$ {totalGeral.toFixed(2)}
              </b>
            </span>
          </div>
        </div>
      </div>

      <style>{`
        .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
        @keyframes modalSlideIn {
          0% { opacity:0; transform: translateY(60px) scale(.97);}
          100% { opacity:1; transform: translateY(0) scale(1);}
        }
        .animate-fadein { animation: fadein 0.25s; }
        @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

export default OSDetalheModal;
